import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SeatsSummary } from '@common/models';
import { CommerceService } from '@common/services/commerce.service';
import { ToastService } from '@common/services/toast.service';
import { extraUsersMonthlyUsd, getExtraUserPriceUsd, MAX_PURCHASED_USERS, minPurchasedUsersForOccupancy } from '@common/utils/extra-users.util';
import { buildExtraUserSeatRows, ExtraSeatOccupant, ExtraSeatRow } from '@common/utils/extra-users-seats.util';
import { UserService } from '@modules/auth/services';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'fina-extra-users-seats',
    templateUrl: './extra-users-seats.component.html',
    styleUrls: ['./extra-users-seats.component.scss'],
})
export class ExtraUsersSeatsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    readonly maxPurchasedUsers = MAX_PURCHASED_USERS;

    /** `card`: franja en próximo pago. `modal`: «Usuarios del comercio» al confirmar el pago. */
    @Input() layout: 'card' | 'modal' = 'card';
    @Input() seats: SeatsSummary | null = null;
    /**
     * Modal: cupos incluidos con el plan ELEGIDO (+ extendUsers), los que usa api-admin al
     * acreditar. Sin él, `seats.included` (plan vigente).
     */
    @Input() selectedIncludedSeats: number | null = null;
    /** `bundle.extraUserPriceUsd` de `GET /api/plans`. Se pinta y se calcula con `resolvedExtraUserPriceUsd`. */
    @Input() extraUserPriceUsd?: number;
    @Input() pendingPurchasedUsers = 0;
    @Input() canEdit = false;
    @Output() pendingPurchasedUsersChange = new EventEmitter<number>();

    /** La lista arranca cerrada: se abre con «Ver quién usa cada cupo». */
    showUsers = false;
    occupants: ExtraSeatOccupant[] = [];
    currentUserId = '';
    ownerId = '';
    private occupantsRequested = false;

    constructor(private userService: UserService, private commerceService: CommerceService, private toastService: ToastService) {}

    ngOnInit(): void {
        if (this.layout !== 'modal') {
            return;
        }
        const current = this.userService.user$.value;
        this.currentUserId = current?._id || '';
        this.ownerId = this.commerceService._commerce$.value?.ownerId || current?._id || '';
        this.commerceService._commerce$.pipe(takeUntil(this.destroy$)).subscribe(commerce => {
            if (commerce?.ownerId) {
                this.ownerId = commerce.ownerId;
            }
        });
        if (current?._id) {
            this.occupants = [current as ExtraSeatOccupant];
        }
    }

    /**
     * `GET /users` solo cuando el comercio abre «Ver quién usa cada cupo», una vez por
     * apertura del modal: los contadores salen de `seats` del bundle, no de la lista.
     */
    private loadOccupants(): void {
        if (this.occupantsRequested) {
            return;
        }
        this.occupantsRequested = true;
        const current = this.userService.user$.value;
        this.userService
            .getUsersAssociated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: { data?: ExtraSeatOccupant[] }) => {
                    const associated = Array.isArray(res?.data) ? res.data : [];
                    const occupants = [...associated];
                    if (current?._id && !occupants.some(user => String(user._id) === String(current._id))) {
                        occupants.unshift(current as ExtraSeatOccupant);
                    }
                    this.occupants = occupants;
                },
                error: (err: any) => {
                    this.occupantsRequested = false;
                    this.occupants = current?._id ? [current as ExtraSeatOccupant] : [];
                    this.toastService.error(err?.error?.title || 'Petición fallida', err?.error?.message || 'No se pudieron cargar los usuarios del comercio.');
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get totalSeats(): number {
        return Number(this.seats?.total || 0);
    }

    get includedSeats(): number {
        const selected = Number(this.selectedIncludedSeats);
        if (this.selectedIncludedSeats != null && Number.isFinite(selected) && selected >= 0) {
            return Math.floor(selected);
        }
        return Number(this.seats?.included || 0);
    }

    get purchasedSeats(): number {
        return Number(this.seats?.purchasedUsers || 0);
    }

    get createdUsers(): number {
        return Number(this.seats?.createdUsers || 0);
    }

    get unassignedSeats(): number {
        return Math.max(0, Number(this.seats?.unassigned || 0));
    }

    /** Precio mensual por cupo: el del bundle o, si falta o no es válido, el fallback local. */
    get resolvedExtraUserPriceUsd(): number {
        return getExtraUserPriceUsd({ extraUserPriceUsd: this.extraUserPriceUsd });
    }

    get extraMonthlyUsd(): number {
        return extraUsersMonthlyUsd(this.purchasedSeats, this.resolvedExtraUserPriceUsd);
    }

    get headerLabel(): string {
        const extras = this.purchasedSeats;
        if (extras > 0) {
            return `${this.totalSeats} cupos (${this.includedSeats} del plan + ${extras})`;
        }
        return `${this.totalSeats} ${this.totalSeats === 1 ? 'cupo' : 'cupos'} (${this.includedSeats} del plan)`;
    }

    get cardTitle(): string {
        return `Tienes ${this.totalSeats} ${this.totalSeats === 1 ? 'cupo' : 'cupos'} de usuario`;
    }

    get cardExtrasLine(): string {
        if (this.purchasedSeats <= 0) {
            return 'Sin usuarios adicionales';
        }
        const monthly = this.extraMonthlyUsd.toFixed(2);
        return `${this.purchasedSeats} ${this.purchasedSeats === 1 ? 'adicional' : 'adicionales'} por $${monthly}/mes`;
    }

    get occupancyLine(): string {
        const unassigned = this.unassignedSeats;
        const created = this.createdUsers;
        const createdLabel = `${created} ${created === 1 ? 'usuario creado' : 'usuarios creados'}`;
        if (unassigned <= 0) {
            return `${createdLabel} · sin cupos libres`;
        }
        return `${createdLabel} · ${unassigned} ${unassigned === 1 ? 'cupo sin asignar' : 'cupos sin asignar'}`;
    }

    /** Cupos tras el pago: incluidos (plan + extendUsers) + adicionales pedidos. */
    get modalTotalSeats(): number {
        return this.includedSeats + Math.max(0, Number(this.pendingPurchasedUsers) || 0);
    }

    get freeSeats(): number {
        return Math.max(0, this.modalTotalSeats - this.createdUsers);
    }

    /** Usuarios que ocupan cupo por encima del total (heredados o datos viejos). */
    get overflowUsers(): number {
        return Math.max(0, this.createdUsers - this.modalTotalSeats);
    }

    get usageLine(): string {
        return `${this.createdUsers} de ${this.modalTotalSeats} ${this.modalTotalSeats === 1 ? 'cupo' : 'cupos'} en uso`;
    }

    get freeSeatsLine(): string {
        const free = this.freeSeats;
        return `Tienes ${free} ${free === 1 ? 'cupo listo' : 'cupos listos'} para crear un usuario.`;
    }

    get fullSeatsLine(): string {
        const overflow = this.overflowUsers;
        if (overflow > 0) {
            const users = overflow === 1 ? 'usuario' : 'usuarios';
            return `Tienes ${overflow} ${users} más que cupos: los más recientes pueden quedar bloqueados. Suma cupos o elimina usuarios antes de pagar.`;
        }
        return this.canEdit ? 'Todos tus cupos están en uso. Suma uno para invitar a alguien más.' : 'Todos tus cupos están en uso.';
    }

    /** Un cupo que ocupa un usuario no se quita: primero se elimina el usuario. */
    get minPurchasedUsers(): number {
        return minPurchasedUsersForOccupancy({
            currentPurchased: this.purchasedSeats,
            includedSeats: this.includedSeats,
            occupiedSeats: this.createdUsers,
        });
    }

    get canDecrement(): boolean {
        return this.canEdit && this.pendingPurchasedUsers > this.minPurchasedUsers;
    }

    get decrementHint(): string {
        if (!this.canEdit || this.canDecrement) {
            return '';
        }
        return this.pendingPurchasedUsers <= 0 ? 'No tienes cupos adicionales.' : 'Para quitar un cupo, primero elimina un usuario asignado.';
    }

    get seatRows(): ExtraSeatRow[] {
        return buildExtraUserSeatRows({
            occupants: this.occupants,
            includedSeats: this.includedSeats,
            pendingPurchasedUsers: this.pendingPurchasedUsers,
            currentUserId: this.currentUserId,
            ownerId: this.ownerId,
        });
    }

    trackSeatRow(_index: number, row: ExtraSeatRow): string {
        return row.key;
    }

    toggleUsers(): void {
        this.showUsers = !this.showUsers;
        if (this.showUsers && this.layout === 'modal') {
            this.loadOccupants();
        }
    }

    /** Tope de servidor (`MAX_PURCHASED_USERS`): más allá `api` responde 400. */
    get atSeatLimit(): boolean {
        return this.pendingPurchasedUsers >= this.maxPurchasedUsers;
    }

    increment(): void {
        if (!this.canEdit || this.atSeatLimit) {
            return;
        }
        this.pendingPurchasedUsersChange.emit(this.pendingPurchasedUsers + 1);
    }

    decrement(): void {
        if (!this.canDecrement) {
            return;
        }
        this.pendingPurchasedUsersChange.emit(this.pendingPurchasedUsers - 1);
    }
}
